import type {
  RankingResult,
  RankingStaticContent,
} from '@core-api/presentation/types/response';
import { ScreenshotDataContext } from '@mentimeter/screenshots/ScreenshotDataContext';
import { useContext, useEffect, useMemo } from 'react';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../../legacy-context/presentationContext';
import { getVisualizationState } from '../../../utils/getVisualizationState';
import { useRankingPreviewResults } from '../hooks/use-ranking-preview-results';
import { useRankingResults } from '../hooks/use-ranking-results';

interface UseRankingPresentationReturn {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: RankingStaticContent['textSizeOffset'];
  choices: Array<{
    id: string;
    label: string;
  }>;
  shouldAnimate: boolean;
  shouldHideResults: boolean;
  results: Array<RankingResult>;
  CanvasVisualizationArea: NonNullable<
    ReturnType<typeof usePresentationContext>['InteractiveCanvasComponents']
  >['CanvasVisualizationArea'];
}

export function useRankingPresentation(): UseRankingPresentationReturn {
  const {
    InteractiveCanvasComponents,
    useConfig,
    useCurrentSession,
    usePace,
    isPreviewResultsEnabled,
  } = usePresentationContext();
  const { enableAnimations, mode } = useConfig();
  const { currentStep } = usePace();
  const slide = usePresentationSlideContext();
  const { isCurrentSessionActive, activeSessionId } = useCurrentSession();
  const staticContent = slide.staticContent as RankingStaticContent;
  const interactiveContent = slide.interactiveContents[0]!;

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    getVisualizationState({ mode, currentStep }) === 'hide-answers';

  const { results, isLoading } = useRankingResults({
    adminKey: slide.slideAdminKey,
    interactiveContentId: interactiveContent.interactiveContentId,
    shouldFetchResults: !shouldHideResults && !isPreviewResultsEnabled,
    sessionId: isCurrentSessionActive ? undefined : activeSessionId,
  });

  const previewResults = useRankingPreviewResults({
    isEnabled: isPreviewResultsEnabled,
    choices: interactiveContent.choices,
  });

  const { setSlideIsReady } = useContext(ScreenshotDataContext);

  useEffect(() => {
    if (mode === 'screenshot' && setSlideIsReady) {
      setSlideIsReady(!isLoading);
    }
  }, [mode, isLoading, setSlideIsReady]);

  const choices = interactiveContent.choices;

  const { CanvasVisualizationArea } = InteractiveCanvasComponents ?? {};

  const choiceOptions = useMemo(
    () =>
      choices.map((choice) => ({
        id: choice.interactiveContentChoiceId,
        label: choice.title,
      })),
    [choices],
  );

  return {
    title: slide.title,
    styledTitle: staticContent.styledTitle as Record<string, unknown>,
    label: staticContent.label,
    fontSizeOffset: staticContent.textSizeOffset,
    longerDescription: interactiveContent.description,
    choices: choiceOptions,
    shouldAnimate: enableAnimations,
    shouldHideResults,
    results: isPreviewResultsEnabled ? previewResults : results,
    CanvasVisualizationArea,
  };
}
